import React, {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {SharedComponentProps} from '../../types/SharedComponentProps';
import {Button} from '../Button/Button';
import {Glyph} from '../../types/Glyph';
import {Icon} from '../Icon/Icon';
import {usePersistedState} from '../../../shared-ui/hooks/usePersistedState';
import styles from './Dialog.module.css';

/* ─── Preset footer button configurations ───────────────────────── */

export type DialogButtons =
  | 'ok'
  | 'close'
  | 'cancel'
  | 'ok-cancel'
  | 'save-cancel'
  | 'delete-cancel'
  | 'archive-cancel'
  | 'accept-cancel'
  | 'yes-cancel'
  | 'done-cancel'
  | 'import-close'
  | 'next-cancel';

/* ─── Props ─────────────────────────────────────────────────────── */

export interface DialogProps extends SharedComponentProps {
  /** Whether the dialog is open. Controlled externally. */
  isOpen: boolean;

  /** Title displayed in the header. Pass `null` to hide the header entirely. */
  title?: string | ReactNode | null;

  /** Optional icon displayed before the title text. */
  icon?: Glyph | ReactNode;

  /** Width of the dialog. Clamped to viewport minus gutter. */
  width?: number;

  /** Height of the dialog in pixels. When omitted, height is determined by content. */
  height?: number;

  /** Footer content: a preset string, custom ReactNode, or null to hide. */
  footer?: DialogButtons | ReactNode | null;

  /** Called when the primary/confirm action is triggered. */
  onConfirm?: () => void;

  /** Called when the dialog is dismissed (cancel, Escape, backdrop click). */
  onDismiss?: () => void;

  /** Called after the close animation/transition completes. */
  afterClosed?: () => void;

  /** Called when the dialog opens (after showModal). */
  onOpen?: () => void;

  /** Disables the primary confirm button. */
  isConfirmDisabled?: boolean;

  /** Shows a loading spinner on the primary button. */
  isLoading?: boolean;

  /** Displays a full loading overlay over the dialog body. */
  showLoadingOverlay?: boolean;

  /** Error message shown below the body content. */
  errorMessage?: string;

  /** Remove default body padding. */
  noPadding?: boolean;

  /** Whether the dialog body scrolls its content. Defaults to true. */
  scrollable?: boolean;

  /** Allow closing the dialog by clicking the backdrop. Default: false. */
  closeOnBackdropClick?: boolean;

  /** Allow the dialog to be dragged by the title bar. Default: true. */
  isDraggable?: boolean;

  /** Allow the dialog to be resized from the edges. Default: true. */
  isResizable?: boolean;

  /** Minimum width when resizing. Default: 200. */
  minWidth?: number;

  /** Minimum height when resizing. Default: 150. */
  minHeight?: number;

  /** Optional class for the body content area. */
  bodyClassName?: string;

  /**
   * Optional key for persisting dialog position/size to localStorage.
   * When provided, the dialog remembers where the user moved/resized it
   * and restores that layout on subsequent opens.
   */
  persistKey?: string;
}

/* ─── Ref handle ────────────────────────────────────────────────── */

export interface DialogRef {
  /** The underlying native <dialog> element. */
  element: HTMLDialogElement | null;
}

/* ─── Resize edge type ──────────────────────────────────────────── */

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/* ─── Component ─────────────────────────────────────────────────── */

export const Dialog = forwardRef<DialogRef, PropsWithChildren<DialogProps>>(
  (props, ref) => {
    const {
      isDraggable = true,
      isResizable = true,
      minWidth = 200,
      minHeight = 150,
      persistKey,
    } = props;

    const dialogRef = useRef<HTMLDialogElement>(null);

    /* ── Persisted layout state ─────────────────────────────────── */

    interface PersistedLayout {
      position: {x: number; y: number} | null;
      size: {width: number; height: number} | null;
    }

    const [persistedLayout, setPersistedLayout] = usePersistedState<PersistedLayout>(
      persistKey ?? '__dialog_noop__',
      {position: null, size: null},
      {enabled: !!persistKey}
    );

    /* ── Drag state ─────────────────────────────────────────────── */

    const dragOffset = useRef({x: 0, y: 0});
    const [dragPosition, setDragPosition] = useState<{x: number; y: number} | null>(
      () => persistKey ? persistedLayout.position : null
    );
    const [isDragging, setIsDragging] = useState(false);

    // Live mirrors of the latest position/size, so the drag/resize *end*
    // handlers persist the current values rather than a stale closure snapshot.
    const dragPositionRef = useRef(dragPosition);
    dragPositionRef.current = dragPosition;

    /* ── Resize state ───────────────────────────────────────────── */

    const resizeRef = useRef<{
      edge: ResizeEdge;
      startX: number;
      startY: number;
      startRect: DOMRect;
    } | null>(null);
    const [resizeSize, setResizeSize] = useState<{width: number; height: number} | null>(
      () => persistKey ? persistedLayout.size : null
    );
    const resizeSizeRef = useRef(resizeSize);
    resizeSizeRef.current = resizeSize;

    useImperativeHandle(ref, () => ({
      get element() {
        return dialogRef.current;
      },
    }));

    /* ── Sync open/close state with native <dialog> ─────────────── */

    const isOpeningRef = useRef(false);

    useLayoutEffect(
      () => {
        const dialog = dialogRef.current;
        if (!dialog) {
          return;
        }

        if (props.isOpen && !dialog.open) {
          isOpeningRef.current = true;
          dialog.showModal();
          requestAnimationFrame(() => {
            isOpeningRef.current = false;
          });
          props.onOpen?.();
        } else if (!props.isOpen && dialog.open) {
          dialog.close();
        }
      },
      [props.isOpen]
    );

    /* ── Reset position/size when dialog closes ─────────────────── */

    useEffect(
      () => {
        const dialog = dialogRef.current;
        if (!dialog) {
          return undefined;
        }

        const handleClose = () => {
          if (!persistKey) {
            setDragPosition(null);
            setResizeSize(null);
          }
          props.afterClosed?.();
        };

        const handleCancel = (e: Event) => {
          // Ignore `cancel` events bubbling up from descendants. A dismissed
          // native file picker (`<input type="file">`) fires a bubbling `cancel`
          // event, which would otherwise close this dialog.
          if (e.target !== dialog) {
            return;
          }

          if (isOpeningRef.current) {
            e.preventDefault();
            return;
          }
          e.preventDefault();
          props.onDismiss?.();
        };

        dialog.addEventListener('close', handleClose);
        dialog.addEventListener('cancel', handleCancel);

        return () => {
          dialog.removeEventListener('close', handleClose);
          dialog.removeEventListener('cancel', handleCancel);
        };
      },
      [props.onDismiss, props.afterClosed, persistKey]
    );

    /* ── Backdrop click ──────────────────────────────────────────── */

    const handleDialogMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDialogElement>) => {
        if (!props.closeOnBackdropClick) {
          return;
        }

        if (e.target === dialogRef.current) {
          props.onDismiss?.();
        }
      },
      [props.closeOnBackdropClick, props.onDismiss]
    );

    /* ── Drag handlers ──────────────────────────────────────────── */

    const handleDragStart = useCallback(
      (e: React.PointerEvent<HTMLElement>) => {
        if (!isDraggable) return;

        const dialog = dialogRef.current;
        if (!dialog) return;

        e.currentTarget.setPointerCapture(e.pointerId);
        setIsDragging(true);

        const rect = dialog.getBoundingClientRect();
        dragOffset.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
      },
      [isDraggable]
    );

    const handleDragMove = useCallback(
      (e: React.PointerEvent<HTMLElement>) => {
        if (!isDragging) return;

        const dialog = dialogRef.current;
        if (!dialog) return;

        const rect = dialog.getBoundingClientRect();
        const margin = 10;

        const newX = e.clientX - dragOffset.current.x;
        const newY = e.clientY - dragOffset.current.y;

        const clampedX = Math.max(margin, Math.min(newX, window.innerWidth - rect.width - margin));
        const clampedY = Math.max(margin, Math.min(newY, window.innerHeight - rect.height - margin));

        setDragPosition({x: clampedX, y: clampedY});
      },
      [isDragging]
    );

    const handleDragEnd = useCallback(
      (e: React.PointerEvent<HTMLElement>) => {
        if (!isDragging) return;
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch { /* pointer may not be captured */ }
        setIsDragging(false);

        if (persistKey) {
          setPersistedLayout(prev => ({...prev, position: dragPositionRef.current}));
        }
      },
      [isDragging, persistKey, setPersistedLayout]
    );

    /* ── Resize handlers ────────────────────────────────────────── */

    const handleResizeStart = useCallback(
      (edge: ResizeEdge, e: React.PointerEvent<HTMLDivElement>) => {
        if (!isResizable) return;

        const dialog = dialogRef.current;
        if (!dialog) return;

        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);

        resizeRef.current = {
          edge,
          startX: e.clientX,
          startY: e.clientY,
          startRect: dialog.getBoundingClientRect(),
        };
      },
      [isResizable]
    );

    const handleResizeMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        const data = resizeRef.current;
        if (!data) return;

        const dx = e.clientX - data.startX;
        const dy = e.clientY - data.startY;
        const {edge, startRect} = data;
        const margin = 10;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let newWidth = startRect.width;
        let newHeight = startRect.height;
        let newX = startRect.left;
        let newY = startRect.top;

        // Horizontal
        if (edge.includes('e')) {
          newWidth = Math.max(minWidth, startRect.width + dx);
          // Clamp: right edge cannot exceed viewport
          newWidth = Math.min(newWidth, vw - newX - margin);
        } else if (edge.includes('w')) {
          const proposedWidth = startRect.width - dx;
          newWidth = Math.max(minWidth, proposedWidth);
          newX = startRect.left + (startRect.width - newWidth);
          // Clamp: left edge cannot go past margin
          if (newX < margin) {
            newX = margin;
            newWidth = startRect.left + startRect.width - margin;
          }
        }

        // Vertical
        if (edge.includes('s')) {
          newHeight = Math.max(minHeight, startRect.height + dy);
          // Clamp: bottom edge cannot exceed viewport
          newHeight = Math.min(newHeight, vh - newY - margin);
        } else if (edge.includes('n')) {
          const proposedHeight = startRect.height - dy;
          newHeight = Math.max(minHeight, proposedHeight);
          newY = startRect.top + (startRect.height - newHeight);
          // Clamp: top edge cannot go past margin
          if (newY < margin) {
            newY = margin;
            newHeight = startRect.top + startRect.height - margin;
          }
        }

        setResizeSize({width: newWidth, height: newHeight});
        setDragPosition({x: newX, y: newY});
      },
      [minWidth, minHeight]
    );

    const handleResizeEnd = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!resizeRef.current) return;
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch { /* pointer may not be captured */ }
        resizeRef.current = null;

        if (persistKey) {
          setPersistedLayout({position: dragPositionRef.current, size: resizeSizeRef.current});
        }
      },
      [persistKey, setPersistedLayout]
    );

    /* ── Footer rendering ───────────────────────────────────────── */

    const renderFooter = (): ReactNode => {
      if (props.footer === null) return null;

      if (props.footer && typeof props.footer !== 'string') {
        return props.footer;
      }

      const preset = (props.footer as DialogButtons) ?? 'ok-cancel';

      const cancelBtn = (label: string = 'Cancel') => (
        <Button
          key="dismiss"
          className={styles.footerButton}
          icon="cancel"
          text={label}
          onClick={props.onDismiss}
        />
      );

      const confirmBtn = (label: string, icon: Glyph = 'accept', variant: 'primary' | 'danger' = 'primary') => (
        <Button
          key="confirm"
          className={styles.footerButton}
          variant={variant}
          icon={icon}
          text={label}
          onClick={props.onConfirm}
          isDisabled={props.isConfirmDisabled}
          isLoading={props.isLoading}
        />
      );

      switch (preset) {
        case 'ok':
          return confirmBtn('OK');
        case 'close':
          return <Button key="close" className={styles.footerButton} icon="cancel" text="Close" onClick={props.onConfirm}/>;
        case 'cancel':
          return cancelBtn();
        case 'ok-cancel':
          return <>{cancelBtn()}{confirmBtn('OK')}</>;
        case 'save-cancel':
          return <>{cancelBtn()}{confirmBtn('Save', 'save')}</>;
        case 'delete-cancel':
          return <>{cancelBtn()}{confirmBtn('Delete', 'delete', 'danger')}</>;
        case 'archive-cancel':
          return <>{cancelBtn()}{confirmBtn('Archive', 'delete', 'danger')}</>;
        case 'accept-cancel':
          return <>{cancelBtn()}{confirmBtn('Accept')}</>;
        case 'yes-cancel':
          return <>{cancelBtn()}{confirmBtn('Yes')}</>;
        case 'done-cancel':
          return <>{cancelBtn()}{confirmBtn('Done')}</>;
        case 'import-close':
          return <>{cancelBtn('Close')}{confirmBtn('Import')}</>;
        case 'next-cancel':
          return <>{cancelBtn()}{confirmBtn('Next')}</>;
        default:
          return null;
      }
    };

    /* ── Title rendering ────────────────────────────────────────── */

    const renderTitle = (): ReactNode => {
      if (props.title === null || !props.title) return null;

      const iconNode = props.icon
        ? typeof props.icon === 'string'
          ? <span className={styles.titleIcon}><span className={styles.iconBadge}><Icon glyph={props.icon} size={16}/></span></span>
          : <span className={styles.titleIcon}><span className={styles.iconBadge}>{props.icon}</span></span>
        : null;

      const titleContent = typeof props.title === 'string'
        ? <span className={styles.titleText}>{props.title}</span>
        : props.title;

      return (
        <>
          {iconNode}
          {titleContent}
        </>
      );
    };

    /* ── Computed styles ─────────────────────────────────────────── */

    const dialogStyle: React.CSSProperties = {
      ...props.style,
      maxWidth: resizeSize ? undefined : props.width,
      ...(props.height && !resizeSize ? {height: props.height} : {}),
      ...(resizeSize ? {width: resizeSize.width, height: resizeSize.height} : {}),
      ...(dragPosition
        ? {
          position: 'fixed',
          margin: 0,
          top: dragPosition.y,
          left: dragPosition.x,
        }
        : {}),
    };

    const hasTitle = props.title !== null && props.title !== undefined;
    const footer = renderFooter();

    /* ── Resize edge helper ─────────────────────────────────────── */

    const resizeHandle = (edge: ResizeEdge) => (
      <div
        className={[styles.resizeHandle, styles[`resize_${edge}` as keyof typeof styles]].join(' ')}
        onPointerDown={(e) => handleResizeStart(edge, e)}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
      />
    );

    /* ── Render ──────────────────────────────────────────────────── */

    // Portal to document.body so the dialog is never a DOM descendant of the
    // component that renders it. Native <dialog> + showModal() only promotes to
    // the top layer for painting — it does not change DOM position — so without
    // a portal, CSS descendant selectors and DOM event bubbling from the host
    // subtree leak into the dialog (e.g. selected-row text colour, row clicks).
    return createPortal(
      <dialog
        ref={dialogRef}
        className={[styles.dialog, props.className].filter(Boolean).join(' ')}
        style={dialogStyle}
        onMouseDown={handleDialogMouseDown}
        aria-label={props.ariaLabel}
        {...(props.attributes as React.HTMLAttributes<HTMLDialogElement>)}
      >
        {hasTitle && (
          <header
            className={[
              styles.header,
              isDraggable ? styles.draggable : null,
            ].filter(Boolean).join(' ')}
            onPointerDown={isDraggable ? handleDragStart : undefined}
            onPointerMove={isDraggable ? handleDragMove : undefined}
            onPointerUp={isDraggable ? handleDragEnd : undefined}
          >
            <div className={styles.titleRow}>
              {renderTitle()}
            </div>
          </header>
        )}

        <div
          className={[
            styles.body,
            props.noPadding ? styles.noPadding : null,
            props.scrollable === false ? styles.noScroll : null,
            props.bodyClassName,
          ].filter(Boolean).join(' ')}
        >
          {props.showLoadingOverlay && (
            <div className={styles.loadingOverlay}>
              <Icon glyph="busy" size={24}/>
            </div>
          )}
          {props.children}
        </div>

        {props.errorMessage && (
          <div className={styles.errorArea} role="alert">
            {props.errorMessage}
          </div>
        )}

        {footer && (
          <footer className={styles.footer}>
            {footer}
          </footer>
        )}

        {/* Resize handles */}
        {isResizable && (
          <>
            {resizeHandle('n')}
            {resizeHandle('s')}
            {resizeHandle('e')}
            {resizeHandle('w')}
            {resizeHandle('ne')}
            {resizeHandle('nw')}
            {resizeHandle('se')}
            {resizeHandle('sw')}
          </>
        )}
      </dialog>,
      document.body
    );
  }
);

Dialog.displayName = 'Dialog';
